import { useQuery } from '@apollo/client';
import { FileAttachmentsWidget, FileAttachmentsWidgetEditFeature } from '@procureai/ui-modules-mui';
import { useTranslation } from 'react-i18next';

import {
  useFileAttachmentsUpdateMutation,
  getAttachmentFragments,
  groupAttachmentsByVisibilityParents,
  getStatusByParentVisibilities,
  FILE_ACCEPTS,
  classificationOptions,
} from '../FileAttachmentsWidget/FileAttachmentsWidget.util';
import { useDemandDetail } from '../Module.hooks';
import { TsrctAttachmentParentVariant, TsrctUploadedByVariant, FileAttachmentFragment } from '@/generated/gql/graphql';
import { I18nNamespace } from '@/legacy/common/types';
import { isDemandEditable } from '@/legacy/util/demand/isDemandEditable';

import { DEMAND_FILE_ATTACHMENTS_DEMAND_QUERY, DEMAND_FILE_ATTACHMENTS_QUERY } from './DemandFileAttachmentsWidget.gql';

export const DemandFileAttachmentsWidget = () => {
  const { demandId } = useDemandDetail();
  const demandParent = {
    id: demandId,
    variant: TsrctAttachmentParentVariant.DEMAND,
  };
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'DemandFileAttachmentsWidget' });
  const { data: demandData, loading: demandLoading } = useQuery(DEMAND_FILE_ATTACHMENTS_DEMAND_QUERY, {
    variables: {
      demandId: demandId!,
    },
  });
  const negotiationCaseIds = demandData?.tesseract.demand.negotiationCases.nodes.map((nc) => nc.id) || [];

  const variables = {
    parents: [demandParent],
    filter: {
      uploadedByVariants: [TsrctUploadedByVariant.INTERNAL_USER],
    },
  };

  const { data, loading: attachmentsLoading } = useQuery(DEMAND_FILE_ATTACHMENTS_QUERY, {
    variables,
  });

  const { save, saving } = useFileAttachmentsUpdateMutation({
    refetchQueries: [{ query: DEMAND_FILE_ATTACHMENTS_QUERY, variables }],
    parent: demandParent,
  });

  const loading = demandLoading || attachmentsLoading;

  const attachments = getAttachmentFragments(data?.tesseract.attachments);
  const visibilitiesByParentId = groupAttachmentsByVisibilityParents(attachments);
  const returnItemStatus = (attachment: FileAttachmentFragment) => {
    return getStatusByParentVisibilities(attachment, visibilitiesByParentId, negotiationCaseIds);
  };

  return (
    <FileAttachmentsWidget
      attachments={attachments}
      loading={loading}
      onSave={save}
      saving={saving}
      getItemStatus={(attachment) => returnItemStatus(attachment as FileAttachmentFragment)}
      isItemDeletable={(attachment) => attachment.parent.id === demandId}
      getItemClassification={(attachment) => attachment.classification}
      isEditable={isDemandEditable(demandData)}
      editFeatures={[FileAttachmentsWidgetEditFeature.UPLOAD, FileAttachmentsWidgetEditFeature.CHANGE_VISIBILITY]}
      uploadButtonLabel={t('upload.buttonLabel')}
      accept={FILE_ACCEPTS}
      classificationOptions={classificationOptions}
    />
  );
};
